import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { fetchLoggedInUser } from "../components/library/LoginUser";
import PaginatedTable from "../components/library/Pagination";
import Loader from "../components/common/Loader";
import DetailModal from "../components/modal/DetailModal";
import { FaEye } from "react-icons/fa";
import format from 'date-fns/format';
import API_BASE_URL from "../config/api";
import LeaveDetailsModal from "../components/modal/LeaveDetailsModal"; 

const EmpDashboard = () => {
  const [employeeName, setEmployeeName] = useState("");
  const [employeeData, setEmployeeData] = useState("");
  const [leaveSummary, setLeaveSummary] = useState({});
  const [leaveStatus, setLeaveStatus] = useState([]);
  const [companyNews, setCompanyNews] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [selectedNews, setSelectedNews] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [requests, setRequests] = useState([]);
  const userRole = sessionStorage.getItem("userrole");
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { token } = useContext(AuthContext);
  const employeeId = sessionStorage.getItem("employeeId");
  const userName = sessionStorage.getItem("username");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    if (!token || !employeeId) return;

    setLoading(true);

    const headers = { Authorization: `Bearer ${token}` };

    const empInfoRequest = axios.get(
      `${API_BASE_URL}/api/EMPDashboard/${employeeId}`,
      { headers }
    );

    const leaveStatusRequest = axios.get(
      `${API_BASE_URL}/api/RequestTimeOff/status/${employeeId}`,
      { headers }
    );

    Promise.all([empInfoRequest, leaveStatusRequest])
      .then(([empRes, leaveRes]) => {
        // Employee Info
        setEmployeeName(empRes.data.firstName);
        setEmployeeData(empRes.data.employeeInfo);
        setCompanyNews(empRes.data.companyNews);
        setUpcomingEvents(empRes.data.upcomingEvents);
        setLeaveSummary(empRes.data.leaveSummary || {});

        // Leave Status
        setLeaveStatus(leaveRes.data.leaves);
      })
      .catch((err) => console.error("Error fetching dashboard data", err))
      .finally(() => setLoading(false));
  }, [employeeId, token]);


  const fetchRequests = async (customFilters = {}) => {
    try {
      const filters = customFilters || {};
      const params = new URLSearchParams();

      if (filters.start) params.append("start", filters.start);
      if (filters.end) params.append("end", filters.end);
      if (filters.type) params.append("type", filters.type);
      if (filters.status) params.append("status", filters.status);

      const query = params.toString();
      const url = `${API_BASE_URL}/api/RequestTimeOff/requests${query ? `?${query}` : ""}`;

      const resp = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const filteredRequests = resp.data.requests.filter(r => r.statusId === "51" && r.employeeId !== userName);
      setRequests(filteredRequests);

    } catch (err) {
      console.error("Error fetching dashboard data", err);
    }
  };


  useEffect(() => {
    fetchRequests();
  }, []);

  const handleOpenModal = (leave) => {

    setSelectedLeave({
      ...leave,
      isRequester: leave.employeeId === userName,
      isAdmin: userRole === "1",
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedLeave(null);
    setIsModalOpen(false);
  };


  return (
    <>
      {loading ? (
        <Loader message="Loading..." />
      ) : (
        <div className="space-y-6">
          {/* Welcome + Buttons */}
          <div className="bg-white border rounded p-6 flex flex-col md:flex-row md:items-center justify-between">
            <h1 className="text-xl font-bold mb-4 md:mb-0">
              Welcome, {`${employeeData.firstName} ${employeeData.lastName}` || "Employee"} 👋
            </h1>
            <div className="flex gap-3">
              <button
                className="custom-btn"
                onClick={() => navigate("/app/timesheet")}
              >
                Timesheet
              </button>
              <button
                className="custom-btn"
                onClick={() => navigate("/app/leavedashboard")}
              >
                Leave Request
              </button>
              <button
                className="custom-btn"
                onClick={() => navigate("/app/employeepayroll")}
              >
                Payslip
              </button>
            </div>
          </div>

          {/* Employee Info + Leave Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Employee Info Card */}
            <div className="bg-white border rounded-lg p-6">
              <h2 className="text-lg font-bold mb-6 text-gray-800 border-b pb-2">EMPLOYEE INFO</h2>
              <div className="space-y-4">
                <div className="flex">
                  <span className="w-28 font-semibold text-gray-600">ID:</span>
                  <span className="text-gray-800">{employeeData.employeeID}</span>
                </div>
                <div className="flex">
                  <span className="w-28 font-semibold text-gray-600">Name:</span>
                  <span className="text-gray-800">{employeeData.firstName} {employeeData.lastName}</span>
                </div>
              </div>
            </div>

            {/* Leave Summary Card */}
            <div className="bg-white border rounded-lg p-6">
              <h2 className="text-lg font-bold mb-6 text-gray-800 border-b pb-2">LEAVE SUMMARY</h2>
              <div className="space-y-4">
                <div className="flex">
                  <span className="w-28 font-semibold text-gray-600">Total Leave:</span>
                  <span className="text-gray-800">{leaveSummary.annualLeave || 0}</span>
                </div>
                <div className="flex">
                  <span className="w-28 font-semibold text-gray-600">Leave Taken:</span>
                  <span className="text-gray-800">{leaveSummary.leaveTaken || 0}</span>
                </div>
                <div className="flex">
                  <span className="w-28 font-semibold text-gray-600">LOP:</span>
                  <span className="text-gray-800">{leaveSummary.lopCount || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {userRole === "1" ? (
            <div className="bg-white border rounded p-4">
              <h2 className="text-lg font-bold mb-2">Pending Leave Approvals</h2>

              <div className="flex overflow-hidden rounded-xl border border-[#dde1e3] bg-white">
                <PaginatedTable
                  columns={[
                    "Employee",
                    "Type",
                    "Start Date",
                    "End Date",
                    "Duration",
                    "Status",
                    "Action",
                  ]}
                  data={requests}
                  pageSize={10}
                  renderRow={(leave) => (
                    <tr key={leave.id} className="border-t border-[#dde1e3]">
                      <td className="px-4 py-2">
                        <label className="font-bold">{leave.employeeId}</label>
                        <br /> {leave.employeeName}
                      </td>
                      <td className="px-4 py-2 text-[#677883]">
                        {leave.leaveType}
                      </td>
                      <td className="px-4 py-2">
                        {" "}
                        {new Date(leave.startDate).toLocaleDateString("en-GB")}
                      </td>
                      <td className="px-4 py-2">
                        {" "}
                        {new Date(leave.endDate).toLocaleDateString("en-GB")}
                      </td>
                      <td className="px-4 py-2">{leave.leaveDays} days</td>
                      <td className="px-4 py-2">
                        {" "}
                        {/* <span className="rounded-lg px-4 py-1 bg-[#f1f3f4] text-sm font-medium"> */}
                        <span
                          className={`rounded-lg px-4 py-1 text-sm font-medium
                          ${leave.status === "Approved" ? "bg-green-100 text-green-700" : ""}
                          ${leave.status === "Rejected" ? "bg-red-100 text-red-700" : ""}
                          ${leave.status === "Requested" ? "bg-yellow-100 text-yellow-700" : ""}
                          ${leave.status === "Cancelled" ? "bg-gray-200 text-gray-700" : ""}
                        `}
                        >
                          {leave.status}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => handleOpenModal(leave)}
                          className="flex min-w-[80px] max-w-[80px] cursor-pointer items-center justify-center hover:bg-black hover:text-white overflow-hidden rounded-full h-8 px-4 bg-[#f1f3f4] text-[#121516] text-sm font-medium leading-normal w-full"
                        >
                          View
                        </button>

                        {/* {leave.status === "Pending" && (
                      <>
                        <button
                          onClick={() => openComment(leave.id, "Approved")}
                          className="px-2 py-1 bg-green-200 mr-1 rounded"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => openComment(leave.id, "Rejected")}
                          className="px-2 py-1 bg-red-200 rounded"
                        >
                          Reject
                        </button>
                      </>
                    )} */}
                      </td>
                    </tr>
                  )}
                />
              </div>

            </div>
          ) : (
            <div className="bg-white border rounded p-4">
              <h2 className="text-lg font-bold mb-2">Leave Requests & Approvals</h2>

              <div className="flex overflow-hidden rounded-xl border border-[#dde1e3] bg-white">
                <PaginatedTable
                  columns={[
                    "Date",
                    "Reason",
                    "Status",
                  ]}
                  data={leaveStatus}
                  pageSize={10}
                  renderRow={(leave, index) => (
                    <tr key={leave.id} className="border-b">
                      <td className="h-[72px] px-4 py-2 text-sm font-normal leading-normal">
                        {format(leave.startDate, "dd/MM/yyyy")} - {format(leave.endDate, "dd/MM/yyyy")}
                      </td>
                      <td className="h-[72px] px-4 py-2 text-sm font-normal leading-normal">{leave.reason}</td>
                      <td
                        className={`h-[72px] px-4 py-2 text-sm font-normal leading-normal ${leave.status === "Approved"
                          ? "text-green-600"
                          : leave.status === "Pending"
                            ? "text-yellow-600"
                            : "text-red-600"
                          }`}
                      >
                        {leave.status}
                      </td>
                    </tr>
                  )}
                />
              </div>

            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div className="bg-white shadow rounded-lg p-5">
              <h2 className="text-xl font-semibold mb-4 border-b pb-2">Company News</h2>
              {companyNews.length > 0 ? (
                <ul className="space-y-3">
                  {companyNews.map((news) => (
                    <li key={news.id} className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg shadow-sm hover:shadow-md transition">
                      <span className="text-gray-800 font-medium">{news.title}</span>
                      <button
                        className="btn-black"
                        onClick={() => setSelectedNews(news)}
                      >
                        View
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500">No company news available</p>
              )}
            </div>

            <div className="bg-white shadow rounded-lg p-5">
              <h2 className="text-xl font-semibold mb-4 border-b pb-2">Upcoming Events</h2>
              {upcomingEvents.length > 0 ? (
                <ul className="space-y-3">
                  {upcomingEvents.map((event) => (
                    <li
                      key={event.id}
                      className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg shadow-sm hover:shadow-md transition"
                    >
                      <span className="text-gray-800 font-medium">
                        {event.title} — {new Date(event.eventDate).toLocaleDateString()}
                      </span>
                      <button
                        className="btn-black"
                        onClick={() => setSelectedEvent(event)}
                      >
                        View
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500">No upcoming events</p>
              )}
            </div>

            {/*
            <div className="bg-white border rounded p-4">
              <h2 className="text-lg font-bold mb-2">Upcoming Events</h2>
              {upcomingEvents.length > 0 ? (
                <ul className="list-disc pl-5">
                  {upcomingEvents.map((event) => (
                    <li key={event.id} className="flex justify-between items-center">
                      <span>
                        {event.title} -{" "}
                        {new Date(event.eventDate).toLocaleDateString()}
                      </span>
                      <button
                        className="btn-black"
                        onClick={() => setSelectedEvent(event)}
                      >
                        View
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No upcoming events</p>
              )}
            </div> */}
          </div>

          <DetailModal
            open={!!selectedNews}
            onClose={() => setSelectedNews(null)}
            title={selectedNews?.title}
            description={selectedNews?.description}
            date={selectedNews?.date}
          />

          <DetailModal
            open={!!selectedEvent}
            onClose={() => setSelectedEvent(null)}
            title={selectedEvent?.title}
            description={selectedEvent?.description}
            date={selectedEvent?.eventDate}
          />

          {selectedLeave && (
            <LeaveDetailsModal
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              leave={selectedLeave}
              onRefresh={fetchRequests}
              isRequester={selectedLeave.isRequester}
              isAdmin={selectedLeave.isAdmin}
            />
          )}
        </div>


      )}
    </>
  );
};

export default EmpDashboard;